import { useEffect, useMemo, useRef, useState } from "react";
import {
  postOfferView,
  getTrackingEvent,
  getCustomVoucher,
} from "../../../../../services/offers/offers.service";
import {
  Offer,
  TemplateOptionSettings,
  OtherSettingsInterface,
  Template,
  CampaignType,
  LayoutOptions,
  MediaType,
  OfferResolutionMedia,
  QueryPostOfferView,
  PlacementStage,
  VoucherType,
  RedeemBy,
  TemplateIcons,
  ICONS_DEFAULT_VALUES,
} from "../../../../../types/global.types";
import TemplateIcon from "../../Icons/info.component";
import PreSelect from "../../PreSelect/PreSelect";
import offerImageSrc from "../../../../../assets/images/offer-listening.png";
import "./style.scss";
import BlockedTag from "../../BlockedTag/blockedTag.component";
import ImageCarousel from "../../../../ImageCarousel/ImageCarousel.component";
import FeaturedOfferVideo, {
  getFeaturedOfferVideoUrl,
} from "../FeaturedOfferVideo/FeaturedOfferVideo";

interface Props {
  offer: Offer;
  slot: number;
  settings: TemplateOptionSettings;
  otherSettings: OtherSettingsInterface | undefined;
  settingsTemplate: Template;
  displayOnly: boolean;
  isAdded: boolean;
  retailerId: string;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaign: CampaignType;
  preEmail: string | undefined;
  setPreEmail?: (email: string) => void;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  hasVoucher: boolean;
  hasAnyVoucherWithType: boolean;
  offerLength: number;
  layoutId: string;
  layoutOptions: LayoutOptions | undefined;
  canAutoSendEmailFromLabel?: boolean;
  canAutoSendEmailFromMainCTA?: boolean;
  onAutoSendEmailFromLabel?: (offer: Offer, slot: number) => void;
  autoSendingOfferId?: string;
  isSeen?: boolean;
}

const FeaturedForMultiple = ({
  offer,
  slot,
  settings,
  campaign,
  settingsTemplate,
  displayOnly,
  retailerId,
  campaignId,
  placementId,
  placementName,
  isAdded,
  otherSettings,
  openSingle,
  onAdd,
  hasVoucher,
  layoutId,
  layoutOptions,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
  isSeen,
}: Props) => {
  const EMAIL_SENT_TOAST_MS = 2000;
  const [showEmailSentToast, setShowEmailSentToast] = useState(false);
  const toastTimeoutRef = useRef<number | null>(null);
  const wasAutoSendingRef = useRef(false);
  const [copyCode, setCopyCode] = useState<boolean>(false);
  const [getCode, setGetCode] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [customVoucher, setCustomVoucher] = useState<string>("");
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);
  const getMediaResolutionByType = (
    type: MediaType,
    offer: Offer
  ): OfferResolutionMedia | undefined => {
    let media: OfferResolutionMedia | undefined = offer.media.resolution.find(
      (r) => r.type === type
    );

    return media;
  };
  const elmRef = useRef<HTMLDivElement>(null);

  const imageUrl = useMemo(() => {
    const media = getMediaResolutionByType(MediaType.featured, offer);
    return media ? media.images[0].filepath : offer.imageUrl || offerImageSrc;
  }, [offer]);

  const title = useMemo(() => {
    const media = getMediaResolutionByType(MediaType.featured, offer);
    return media?.title || offer.media.featured.title || offer.title;
  }, [offer]);

  const description = useMemo(() => {
    const media = getMediaResolutionByType(MediaType.featured, offer);

    return (
      media?.description ||
      offer.media.featured.description ||
      offer.description
    );
  }, [offer]);

  const handleAdd = (
    e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    const value =
      e.target instanceof HTMLInputElement ? e.target.checked : false;
    if (displayOnly && !offer.tagBlocked.isBlocked) {
      onAdd(offer, value, slot);
      value && handleOpenEmailInput();
    }
  };

  function observeIframeVisibility(elm: HTMLElement, callback: () => void) {
    if (!elm) {
      console.error(`Element not found.`);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback();
            // Optionally, stop observing after the first intersection
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null, // Use the viewport as the root
        rootMargin: "0px",
        threshold: 0.5, // Trigger when at least 50% of the iframe is visible
      }
    );

    observer.observe(elm);
  }

  const setDefaultOfferImpressionEvent = async () => {
    if (offer) {
      const data: QueryPostOfferView = {
        offers: [{ id: offer.id, slot }],
        campaignId,
        campaignName: campaign.name || "",
        companyId: campaign.company_Id,
        placementId,
        placementName,
        pageUrl: "",
        templateId: settingsTemplate.identifier,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        layoutId,
      };

      await postOfferView(data);
    }
  };

  useEffect(() => {
    if (!isSeen || !elmRef.current) {
      return;
    }

    observeIframeVisibility(elmRef.current, () => {
      setDefaultOfferImpressionEvent();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeen]);


  const handleOpenEmailInput = () => {
    if (
      campaign?.campaignGeneralOptions.showAddToOrder &&
      campaign?.campaignGeneralOptions.emailCaptureOnOffers &&
      campaign?.campaignGeneralOptions.emailCaptureOnAddToOrder
    ) {
      openSingle(offer, false, true, slot);
    }
  };

  const handleGetGift = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    url: string
  ) => {
    if (offer?.voucher?.voucherType === VoucherType.CUSTOM && !getCode) {
      return;
    }

    if (
      event.target instanceof HTMLElement &&
      !event.target.classList.contains("item-info-icon") &&
      !event.target.classList.contains("item-email-icon") &&
      !event.target.classList.contains("copy-box") &&
      !event.target.classList.contains("item-prevent-click") &&
      !displayOnly &&
      offer.offerSettings.redeemBy !== RedeemBy.email
    ) {
      window.open(
        url + `&tid=${settingsTemplate.identifier}&slot=${slot}`,
        "_blank"
      );
    }
  };

  const handleClickInfo = (
    event: React.MouseEvent | React.KeyboardEvent<HTMLElement>
  ) => {
    event.preventDefault();
    openSingle(offer, false, false, slot);
    getTrackingEvent({
      event: "onreadmoreclick",
      offerId: offer.id,
      retailerId,
      campaignId,
      placementId,
      placementName,
      tid: settingsTemplate.identifier,
      slot,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      pltid: layoutId,
    });
  };

  const handleClickShare = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    event.preventDefault();
    openSingle(offer, false, false, slot, true);
  };

  const handleSaveEmail = async (
    event: React.MouseEvent | React.KeyboardEvent<HTMLElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (
      canAutoSendEmailFromLabel &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled
    ) {
      await onAutoSendEmailFromLabel(offer, slot);
      return;
    }
    openSingle(offer, true, false, slot);
  };

  const handleCopyHtml = () => {
    window.focus();
    navigator.clipboard?.writeText(
      customVoucher ? customVoucher : offer.voucher?.code
    );
    setCopied(true);
    setCopyCode(false);
    setTimeout(() => {
      setCopied(false);
    }, 1000); // Show the message for 1 second
  };

  const handleGetCode = async () => {
    setVoucherLoading(true);
    const customVoucher = await getCustomVoucher({
      offerId: offer.id,
      campaignId: campaign.id,
      campaignName: campaign.name,
      companyId: campaign.company_Id,
      placementId: placementId,
      placementName: placementName,
      templateId: settingsTemplate.identifier,
      layoutId: layoutId,
      orderId: offer.orderId,
    });
    setCustomVoucher(customVoucher?.data?.result);
    setGetCode(true);
    setVoucherLoading(false);
  };

  const getVoucherText = () => {
    return customVoucher ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>

        <span style={{ fontWeight: "600" }}>{customVoucher}</span>
      </>
    ) : offer.voucher?.code ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>
        <span style={{ fontWeight: "600" }}>{offer.voucher?.code}</span>
      </>
    ) : (
      <span style={{ fontWeight: "400" }}>Sorry, code just expired!</span>
    );
  };

  const handleMainCtaAutoSend = () => {
    if (
      canAutoSendEmailFromMainCTA &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled &&
      !autoSendingOfferId
    ) {
      onAutoSendEmailFromLabel(offer, slot);
    }
  };

  const shouldShowEmailIcon =
    !displayOnly &&
    campaign.campaignGeneralOptions.saveOfferOptions.allowSavingOffers &&
    campaign.campaignGeneralOptions.saveOfferOptions.allowEmail &&
    !offer.offerSettings?.bundleOptions?.isBundled &&
    offer.offerSettings.redeemBy === RedeemBy.webemail &&
    (layoutOptions?.showEmail ?? true);

  const triggerEmailSentToast = () => {
    setShowEmailSentToast(true);
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setShowEmailSentToast(false);
    }, EMAIL_SENT_TOAST_MS);
  };

  useEffect(() => {
    if (autoSendingOfferId === offer.id) {
      wasAutoSendingRef.current = true;
      return;
    }

    if (wasAutoSendingRef.current && canAutoSendEmailFromLabel) {
      wasAutoSendingRef.current = false;
      triggerEmailSentToast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendingOfferId, offer.id, canAutoSendEmailFromLabel]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`feature-multiple-desktop ${displayOnly && campaign?.campaignGeneralOptions.showAddToOrder
        ? "display"
        : "getbutton"
        }
      ${settingsTemplate.identifier === "template-esuper-post" ||
          settingsTemplate.identifier === "template-esuper-pre" ||
          settingsTemplate.identifier === "template-category-post" ||
          settingsTemplate.identifier === "template-category-pre"
          ? "y-margin"
          : "normal-margin"
        }
      `}
      ref={elmRef}
    >
      {showEmailSentToast && (
        <div className="template-email-toast">Email sent successfully</div>
      )}
      <input
        className="hidden-input"
        id={offer.id}
        type="checkbox"
        disabled={
          !displayOnly || !campaign?.campaignGeneralOptions.showAddToOrder
        }
        checked={isAdded}
        onChange={handleAdd}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleAdd(e);
          }
        }}
      />

      {(layoutOptions?.showBadge ?? true) && (
        <span
          style={{
            position: "absolute",
            top: "-8.5px",
            left: "20px",
            fontSize: "10px",
            textTransform: "uppercase",
            color: offer.badge?.fontColor,
            backgroundColor: offer.badge?.backgroundColor,
            padding: "0 6px",
            borderRadius: "3px",
            fontWeight: "600",
            zIndex: 10,
          }}
          className="badge-settings"
        >
          {offer.badge?.name}
        </span>
      )}

      <label
        htmlFor={offer.id}
        className="item-layout"
        onClick={(e) => handleGetGift(e, offer.trackingUrl)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleGetGift(e, offer.trackingUrl);
          }
        }}
        style={{
          backgroundColor: settings?.dealLayout.backgroundColor,
          fontSize: settings?.dealLayout.fontSize,
          color: settings?.dealLayout.textColor,
          borderRadius: `${settings?.mainLayout.borderRadius}px`,
          border: `1px solid ${isAdded
            ? settings?.mainLayout?.highlightColor ||
            settings?.dealLayout.borderColor ||
            "#3ab78f"
            : offer.badge?.useBorder && (layoutOptions?.showBadge ?? true)
              ? offer.badge?.backgroundColor
              : settings?.dealLayout.borderColor || "#d6dae5"
            }`,
          paddingBottom: hasVoucher ? "20px" : "0",
          position: "relative",
        }}
      >
        {offer.tagBlocked.isBlocked && (
          <BlockedTag tagBlocked={offer.tagBlocked} />
        )}

        <div className="item-image">
          <FeaturedOfferVideo
            showVideo={!!settings?.mainLayout?.showFeaturedVideo}
            videoUrl={getFeaturedOfferVideoUrl(offer)}
          >
            {offer.media.featured.images.length > 0 ? (
              <ImageCarousel
                images={offer.media.featured.images.map(
                  (image) => image.filepath
                )}
                showPagination={true}
                altPrefix="feature"
                style={{ width: "100%", height: "auto" }}
              />
            ) : (
              <img
                className={`image-large`}
                src={imageUrl}
                alt=""
                role="presentation"
                style={{
                  borderRadius: `${settings?.mainLayout.borderRadius}px`,
                }}
              />
            )}
          </FeaturedOfferVideo>
        </div>

        <div className={`item-content`}>
          <div className="item-content-info">
            <div className="item-title">
              <span
                className={`item-title-text max-2lines `}
                style={{
                  color: settings?.dealLayout.textColor,
                  fontSize: `${Number(settings?.dealLayout.fontSize) + 1}px`,
                }}
              >
                {title}
              </span>
            </div>
            <div
              className="item-description max-2lines"
              style={{
                color: settings?.dealLayout.textColor,
                fontSize: `${settings?.dealLayout.fontSize}px`,
              }}
            >
              {description}
            </div>
          </div>

          <div className="item-get">
            <div className="item-get-center">
              {displayOnly ? (
                <>
                  {campaign?.campaignGeneralOptions.showAddToOrder && (
                    <PreSelect
                      offerId={offer.id}
                      buttonFontWeight={settings?.dealLayout.buttonFontWeight}
                      buttonFontSize={settings?.dealLayout.buttonFontSize}
                      showCheckboxSelect={otherSettings?.showCheckboxSelect}
                      checked={isAdded}
                      highlightColor={settings?.mainLayout?.highlightColor}
                      borderColor={settings?.dealLayout?.borderColor}
                      selectedButtonText={otherSettings?.selectedButtonText}
                      notSelectedButtonText={
                        otherSettings?.notSelectedButtonText
                      }
                      settings={otherSettings}
                    />
                  )}
                </>
              ) : (
                <>
                  {offer?.voucher?.voucherType === VoucherType.CUSTOM &&
                    !getCode ? (
                    <div
                      className="dashed-button copy-box"
                      onClick={handleGetCode}
                      tabIndex={0}
                      role="button"
                      aria-label="Get code"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          handleGetCode();
                        }
                      }}
                      style={{
                        fontSize: `${settings?.dealLayout.buttonFontSize}px`,
                        position: "relative",
                      }}
                    >
                      {voucherLoading ? (
                        <svg
                          className="spin"
                          viewBox="0 0 1024 1024"
                          focusable="false"
                          data-icon="loading"
                          width="1em"
                          height="1em"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                        </svg>
                      ) : (
                        "Get code"
                      )}
                    </div>
                  ) : (
                    offer.offerSettings.redeemBy === RedeemBy.webemail && (
                      <div
                        className="item-action-get max-1lines"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleGetGift(e, offer.trackingUrl);
                          handleMainCtaAutoSend();
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label="Get offer now"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGetGift(e, offer.trackingUrl);
                            handleMainCtaAutoSend();
                          }
                        }}
                        style={{
                          color: settings?.dealLayout.buttonTextColor,
                          fontSize: `${settings?.dealLayout.buttonFontSize}px`,
                          fontWeight: settings?.dealLayout.buttonFontWeight,
                          backgroundColor:
                            settings?.dealLayout.buttonBackgroundColor,
                          borderRadius: settings?.dealLayout.buttonRounding,
                          border: `1px solid ${settings?.dealLayout.buttonBorderColor ||
                            settings?.dealLayout.buttonBackgroundColor
                            }`,
                          textTransform:
                            settings?.dealLayout.buttonTextTransform,
                        }}
                      >
                        {offer?.offerCTAText || "Get offer now"}
                      </div>
                    )
                  )}
                </>
              )}
            </div>

            <div className="item-get-icons">
              <div
                className="item-info-icon"
                onClick={handleClickInfo}
                tabIndex={0}
                role="button"
                aria-label="More information about offer"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    handleClickInfo(e);
                  }
                }}
              >
                {!settings?.dealLayout.icons ||
                  settings?.dealLayout.icons === TemplateIcons.default ? (
                  <TemplateIcon
                    icon={
                      settings?.dealLayout?.iconInfo?.value ||
                      ICONS_DEFAULT_VALUES.iconInfo
                    }
                    color={settings?.dealLayout.textColor || "#888888"}
                  />
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                      fill={settings?.dealLayout.textColor}
                    />
                  </svg>
                )}
              </div>

              {shouldShowEmailIcon && (
                <div
                  className="item-email-icon item-info-icon item-prevent-click"
                  onClick={(e) => {
                    void handleSaveEmail(e);
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Save offer to email"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                      void handleSaveEmail(e);
                    }
                  }}
                >
                  {!settings?.dealLayout.icons ||
                    settings?.dealLayout.icons === TemplateIcons.default ? (
                    <TemplateIcon
                      icon={
                        settings?.dealLayout?.iconEmail?.value ||
                        ICONS_DEFAULT_VALUES.iconEmail
                      }
                      color={settings?.dealLayout.textColor || "#888888"}
                    />
                  ) : (
                    <svg
                      width="20"
                      height="16"
                      viewBox="0 0 20 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19 0H1C0.45 0 0 0.45 0 1V15C0 15.55 0.45 16 1 16H19C19.55 16 20 15.55 20 15V1C20 0.45 19.55 0 19 0ZM18.38 1L10 9.07996L1.69 1H18.38ZM1 15V1.72L10 10.47L19 1.8V15H1Z"
                        fill={settings?.dealLayout?.textColor || "#888888"}
                      />
                    </svg>
                  )}
                </div>
              )}

              {!displayOnly &&
                campaign.sharingOptions?.allowSharing &&
                campaign.sharingOptions.sharingPlatforms?.platforms?.length >
                0 &&
                offer?.offerSettings?.allowShare &&
                (layoutOptions?.showShare ?? true) && (
                  <div
                    className="item-share-icon item-info-icon"
                    onClick={handleClickShare}
                    tabIndex={0}
                    role="button"
                    aria-label="Share offer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        handleClickShare(e);
                      }
                    }}
                  >
                    <TemplateIcon
                      icon={
                        settings?.dealLayout?.iconShare?.value ||
                        ICONS_DEFAULT_VALUES.iconShare
                      }
                      color={settings?.dealLayout.textColor || "#888888"}
                    />
                  </div>
                )}
            </div>
          </div>
        </div>

        {offer.voucher?.voucherType && (
          <span
            style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              fontSize: "11px",
              color: settings?.dealLayout.buttonTextColor,
              backgroundColor: settings?.dealLayout.buttonBackgroundColor,
              fontWeight: "600",
              textAlign: "center",
              width: "100%",
              height: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderBottomLeftRadius:
                typeof settings?.mainLayout.borderRadius === "number"
                  ? `${settings?.mainLayout.borderRadius}px`
                  : "0 0 7px 7px",
              borderBottomRightRadius:
                typeof settings?.mainLayout.borderRadius === "number"
                  ? `${settings?.mainLayout.borderRadius}px`
                  : "0 0 7px 7px",
            }}
            onMouseEnter={() => setCopyCode(true)}
            onMouseLeave={() => {
              setCopyCode(false);
            }}
            onClick={() => handleCopyHtml()}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                handleCopyHtml();
              }
            }}
          >
            {offer.voucher?.voucherType === VoucherType.CUSTOM && !getCode ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  borderBottomLeftRadius: displayOnly
                    ? `${typeof settings?.mainLayout.borderRadius === "number"
                      ? settings?.mainLayout.borderRadius
                      : 10
                    }px`
                    : "10px",
                  borderBottomRightRadius: displayOnly
                    ? `${typeof settings?.mainLayout.borderRadius === "number"
                      ? settings?.mainLayout.borderRadius
                      : 10
                    }px`
                    : "10px",
                }}
                className="copy-box"
              >
                Code:
                <svg
                  style={{
                    marginLeft: "5px",
                  }}
                  width="70"
                  height="16"
                  viewBox="0 0 70 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="70" height="16" fill="" />
                  <circle
                    cx="4"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="10"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="52"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="58"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="16"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="22"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="28"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="34"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="40"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                  <circle
                    cx="46"
                    cy="8"
                    r="2"
                    fill={settings?.dealLayout.buttonTextColor || "white"}
                  />
                </svg>
              </div>
            ) : (
              <>
                {copyCode ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      fontWeight: "400",
                      cursor: "pointer",
                    }}
                    onClick={handleCopyHtml}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        handleCopyHtml();
                      }
                    }}
                    className="copy-box"
                  >
                    {copied ? (
                      <>
                        <svg
                          width="8"
                          height="6"
                          viewBox="0 0 8 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            marginRight: "5px",
                          }}
                        >
                          <path
                            d="M1 3L3 5L7 1"
                            stroke={
                              settings?.dealLayout.buttonTextColor || "white"
                            }
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Copied successfully
                      </>
                    ) : (
                      <>
                        <svg
                          width="11"
                          height="10"
                          viewBox="0 0 11 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            marginRight: "5px",
                          }}
                        >
                          <path
                            d="M7.75 3V2C7.75 1.73478 7.63147 1.48043 7.42049 1.29289C7.20952 1.10536 6.92337 1 6.625 1H2.125C1.82663 1 1.54048 1.10536 1.3295 1.29289C1.11853 1.48043 1 1.73478 1 2V6C1 6.26522 1.11853 6.51957 1.3295 6.70711C1.54048 6.89464 1.82663 7 2.125 7H3.25M4.375 3H8.875C9.49632 3 10 3.44772 10 4V8C10 8.55228 9.49632 9 8.875 9H4.375C3.75368 9 3.25 8.55228 3.25 8V4C3.25 3.44772 3.75368 3 4.375 3Z"
                            stroke={
                              settings?.dealLayout.buttonTextColor || "white"
                            }
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Copy Code
                      </>
                    )}
                  </div>
                ) : copied ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      fontWeight: "400",
                    }}
                    className="copy-box"
                  >
                    <svg
                      width="8"
                      height="6"
                      viewBox="0 0 8 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        marginRight: "5px",
                      }}
                    >
                      <path
                        d="M1 3L3 5L7 1"
                        stroke={settings?.dealLayout.buttonTextColor || "white"}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Copied successfully
                  </div>
                ) : (
                  getVoucherText()
                )}
              </>
            )}
          </span>
        )}
      </label>
    </div>
  );
};

export default FeaturedForMultiple;
